'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    addContractTerm,
    ContractTerm,
    Contract as ContractType,
    createContract,
    deleteContractTerm,
    getContract,
    updateContractStatus,
    updateContractTerm,
} from '@/lib/api/administrative/contracts-api'
import { createSignDocument } from '@/lib/api/administrative/sign-api'
import { useAuth } from '@/store/hooks'
import { createRef, lazy, Suspense, useEffect, useRef, useState } from 'react'
import { BsFilePdf } from 'react-icons/bs'
import {
    MdAdd,
    MdArrowBack,
    MdCheck,
    MdClose,
    MdDelete,
    MdDownload,
    MdDraw,
    MdEdit,
    MdSend,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
/**
 * Carregado sob demanda: o leitor arrasta o pdf.js inteiro, e este import era
 * estático — o que colocava mais de 1 MB no pacote inicial de TODA a aplicação,
 * inclusive para quem nunca abre um contrato.
 */
const ContractPDFViewer = lazy(() =>
    import('./contract-pdf-viewer').then(module => ({
        default: module.ContractPDFViewer,
    })),
)

type TermType = 'CLAUSE' | 'PARAGRAPH' | 'INCISO' | 'ALINEA' | 'HEADER' | 'FOOTER'

function getTermTypeLabel(type: string) {
    const types: Record<string, string> = {
        CLAUSE: 'Cláusula',
        PARAGRAPH: 'Parágrafo',
        INCISO: 'Inciso',
        ALINEA: 'Alínea',
        HEADER: 'Cabeçalho',
        FOOTER: 'Rodapé',
    }
    return types[type] || type
}

function isHeaderOrFooter(type: string) {
    return type === 'HEADER' || type === 'FOOTER'
}

export default function ContractPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const isNew = id === 'novo'

    const [contract, setContract] = useState<ContractType | null>(null)
    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [creatingContract, setCreatingContract] = useState(false)
    const [showAddTermModal, setShowAddTermModal] = useState(false)
    const [showEditTermModal, setShowEditTermModal] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [selectedTerm, setSelectedTerm] = useState<ContractTerm | null>(null)
    const [showPdfView, setShowPdfView] = useState(false)
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
    const [generatingPdf, setGeneratingPdf] = useState(false)
    const [showFinalizeModal, setShowFinalizeModal] = useState(false)
    const [showSendToSignModal, setShowSendToSignModal] = useState(false)
    const [sendingToSign, setSendingToSign] = useState(false)

    const [termForm, setTermForm] = useState({
        number: '1',
        title: '',
        content: '',
        type: 'CLAUSE' as TermType,
    })

    const addTermModalRef = createRef<HTMLDialogElement>()
    const editTermModalRef = createRef<HTMLDialogElement>()
    const creatingRef = useRef(false)

    useEffect(() => {
        if (isNew) {
            if (creatingRef.current) return
            creatingRef.current = true
            handleCreateContract()
        } else if (id) {
            loadData()
        }
    }, [id])

    useEffect(() => {
        if (showAddTermModal) {
            addTermModalRef.current?.showModal()
        } else {
            addTermModalRef.current?.close()
        }
    }, [showAddTermModal])

    useEffect(() => {
        if (showEditTermModal) {
            editTermModalRef.current?.showModal()
        } else {
            editTermModalRef.current?.close()
        }
    }, [showEditTermModal])

    async function loadData() {
        try {
            setLoading(true)
            const response = await getContract(Number(id))
            if (response.success) {
                setContract(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar contrato')
                navigate('/administrative/contracts')
            }
        } catch {
            toast.error('Erro ao carregar contrato')
            navigate('/administrative/contracts')
        } finally {
            setLoading(false)
        }
    }

    async function handleCreateContract() {
        try {
            setCreatingContract(true)
            const response = await createContract()
            if (response.success) {
                navigate(`/administrative/contracts/${response.result.id}`, { replace: true })
            } else {
                toast.error(response.error || 'Erro ao criar contrato')
                navigate('/administrative/contracts')
            }
        } catch {
            toast.error('Erro ao criar contrato')
            navigate('/administrative/contracts')
        } finally {
            setCreatingContract(false)
        }
    }

    async function handleAddTerm() {
        if (!contract) return

        const isSpecialType = isHeaderOrFooter(termForm.type)

        if (!isSpecialType && (!termForm.title || !termForm.content)) {
            toast.error('Preencha todos os campos obrigatórios')
            return
        }

        if (isSpecialType && !termForm.content) {
            toast.error('Preencha o conteúdo')
            return
        }

        try {
            setSaving(true)
            const response = await addContractTerm(contract.id, {
                number: isSpecialType ? '0' : termForm.number,
                title: isSpecialType ? '' : termForm.title,
                content: termForm.content,
                type: termForm.type,
            })
            if (response.success) {
                toast.success('Cláusula adicionada com sucesso')
                setShowAddTermModal(false)
                resetTermForm()
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao adicionar cláusula')
            }
        } catch {
            toast.error('Erro ao adicionar cláusula')
        } finally {
            setSaving(false)
        }
    }

    async function handleUpdateTerm() {
        if (!contract || !selectedTerm) return

        const isSpecialType = isHeaderOrFooter(termForm.type)

        if (!isSpecialType && (!termForm.title || !termForm.content)) {
            toast.error('Preencha todos os campos obrigatórios')
            return
        }

        if (isSpecialType && !termForm.content) {
            toast.error('Preencha o conteúdo')
            return
        }

        try {
            setSaving(true)
            const response = await updateContractTerm(selectedTerm.id, {
                number: isSpecialType ? '0' : termForm.number,
                title: isSpecialType ? '' : termForm.title,
                content: termForm.content,
                type: termForm.type,
            })
            if (response.success) {
                toast.success('Cláusula atualizada com sucesso')
                setShowEditTermModal(false)
                setSelectedTerm(null)
                resetTermForm()
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao atualizar cláusula')
            }
        } catch {
            toast.error('Erro ao atualizar cláusula')
        } finally {
            setSaving(false)
        }
    }

    async function handleDeleteTerm() {
        if (!selectedTerm) return

        try {
            setSaving(true)
            const response = await deleteContractTerm(selectedTerm.id)
            if (response.success) {
                toast.success('Cláusula removida com sucesso')
                setShowDeleteConfirm(false)
                setSelectedTerm(null)
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao remover cláusula')
            }
        } catch {
            toast.error('Erro ao remover cláusula')
        } finally {
            setSaving(false)
        }
    }

    function resetTermForm() {
        setTermForm({
            number: String((contract?.terms?.length || 0) + 1),
            title: '',
            content: '',
            type: 'CLAUSE',
        })
    }

    function canEditOrDeleteTerm(term: ContractTerm): boolean {
        if (term.fixed || term.adminOnly) {
            return user?.userType === 'MASTER'
        }
        return true
    }

    function canEditContract(): boolean {
        if (!contract) return false
        return contract.status === 'DRAFT'
    }

    function openEditModal(term: ContractTerm) {
        if (!canEditOrDeleteTerm(term)) {
            toast.error('Sem permissão para editar esta cláusula')
            return
        }
        setSelectedTerm(term)
        setTermForm({
            number: term.number,
            title: term.title,
            content: term.content,
            type: term.type,
        })
        setShowEditTermModal(true)
    }

    function openDeleteModal(term: ContractTerm) {
        if (!canEditOrDeleteTerm(term)) {
            toast.error('Sem permissão para remover esta cláusula')
            return
        }
        setSelectedTerm(term)
        setShowDeleteConfirm(true)
    }

    async function handleGeneratePdf() {
        if (!contract || !contract.terms || contract.terms.length === 0) {
            toast.error('Não há cláusulas para gerar o PDF')
            return
        }

        try {
            setGeneratingPdf(true)
            setShowPdfView(true)

            const { pdf } = await import('@react-pdf/renderer')
            const { ContractPDFDocument } = await import(
                './contract-pdf-document'
            )

            const companyName =
                user?.company?.tradingName || user?.company?.name || 'Empresa'

            const pdfDocument = (
                <ContractPDFDocument
                    terms={contract.terms}
                    contractId={contract.id}
                    companyName={companyName}
                />
            )

            const blob = await pdf(pdfDocument).toBlob()
            setPdfBlob(blob)
            toast.success('PDF gerado com sucesso!')
        } catch {
            toast.error('Erro ao gerar PDF')
            setShowPdfView(false)
        } finally {
            setGeneratingPdf(false)
        }
    }

    function handleDownloadPdf() {
        if (!pdfBlob) return

        const url = URL.createObjectURL(pdfBlob)
        const link = document.createElement('a')
        link.href = url
        link.download = `Contrato_${contract?.id}_${new Date().toISOString().split('T')[0]}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    async function handleFinalizeContract() {
        if (!contract) return
        try {
            setSaving(true)
            const response = await updateContractStatus(contract.id, 'APPROVED')
            if (response.success) {
                toast.success('Contrato finalizado com sucesso!')
                setShowFinalizeModal(false)
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao finalizar contrato')
            }
        } catch {
            toast.error('Erro ao finalizar contrato')
        } finally {
            setSaving(false)
        }
    }

    async function handleSendToSign() {
        if (!contract) return

        try {
            setSendingToSign(true)

            const { pdf } = await import('@react-pdf/renderer')
            const { ContractPDFDocument } = await import(
                './contract-pdf-document'
            )

            const companyName =
                user?.company?.tradingName || user?.company?.name || 'Empresa'

            const pdfDocument = (
                <ContractPDFDocument
                    terms={contract.terms}
                    contractId={contract.id}
                    companyName={companyName}
                />
            )

            const blob = await pdf(pdfDocument).toBlob()
            const file = new File(
                [blob],
                `Contrato_${contract.id}.pdf`,
                { type: 'application/pdf' },
            )

            const data = {
                origin: 'CONTRACT',
                originId: contract.id,
                description: `Contrato #${contract.id}`,
            }

            const formData = new FormData()
            formData.append('file', file)
            formData.append('data', JSON.stringify(data))

            const response = await createSignDocument(formData)
            if (response.success && response.result) {
                toast.success(
                    'Documento criado! Adicione os signatários para enviar.',
                )
                setShowSendToSignModal(false)
                navigate(`/commerce/subscriptions/${response.result.id}`)
            } else {
                toast.error(
                    response.error || 'Erro ao criar documento de assinatura',
                )
            }
        } catch {
            toast.error('Erro ao criar documento de assinatura')
        } finally {
            setSendingToSign(false)
        }
    }

    async function handleBackToDraft() {
        if (!contract) return
        try {
            setSaving(true)
            const response = await updateContractStatus(contract.id, 'DRAFT')
            if (response.success) {
                toast.success('Contrato retornado para rascunho!')
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao retornar para rascunho')
            }
        } catch {
            toast.error('Erro ao retornar para rascunho')
        } finally {
            setSaving(false)
        }
    }

    if (loading || creatingContract) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    {creatingContract
                        ? 'Criando contrato...'
                        : 'Carregando contrato...'}
                </p>
            </div>
        )
    }

    if (!contract) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <p className="text-error">Contrato não encontrado</p>
                <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/administrative/contracts')}
                >
                    Voltar
                </button>
            </div>
        )
    }

    const renderTermForm = (isEdit: boolean) => (
        <div className="space-y-4">
            <div className="flex gap-4">
                {!isHeaderOrFooter(termForm.type) && (
                    <div className="form-control flex-1">
                        <label className="label">
                            <span className="label-text font-medium">
                                Número
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered"
                            value={termForm.number}
                            onChange={e =>
                                setTermForm({
                                    ...termForm,
                                    number: e.target.value,
                                })
                            }
                        />
                    </div>
                )}
                <div className="form-control flex-1">
                    <label className="label">
                        <span className="label-text font-medium">Tipo</span>
                    </label>
                    <select
                        className="select select-bordered"
                        value={termForm.type}
                        onChange={e =>
                            setTermForm({
                                ...termForm,
                                type: e.target.value as TermType,
                            })
                        }
                    >
                        <option value="CLAUSE">Cláusula</option>
                        <option value="PARAGRAPH">Parágrafo</option>
                        <option value="INCISO">Inciso</option>
                        <option value="ALINEA">Alínea</option>
                        <option value="HEADER">Cabeçalho</option>
                        <option value="FOOTER">Rodapé</option>
                    </select>
                </div>
            </div>

            {!isHeaderOrFooter(termForm.type) && (
                <div className="form-control">
                    <label className="label">
                        <span className="label-text font-medium">Título</span>
                    </label>
                    <input
                        type="text"
                        className="input input-bordered"
                        placeholder="Ex: Do Objeto"
                        value={termForm.title}
                        onChange={e =>
                            setTermForm({
                                ...termForm,
                                title: e.target.value,
                            })
                        }
                    />
                </div>
            )}

            <div className="form-control">
                <label className="label">
                    <span className="label-text font-medium">Conteúdo</span>
                </label>
                <textarea
                    className="textarea textarea-bordered min-h-[150px] w-full"
                    placeholder="Conteúdo da cláusula..."
                    value={termForm.content}
                    onChange={e =>
                        setTermForm({
                            ...termForm,
                            content: e.target.value,
                        })
                    }
                />
            </div>

            <div className="flex justify-end gap-2">
                <button
                    className="btn btn-ghost"
                    onClick={() => {
                        if (isEdit) {
                            setShowEditTermModal(false)
                            setSelectedTerm(null)
                        } else {
                            setShowAddTermModal(false)
                        }
                        resetTermForm()
                    }}
                    disabled={saving}
                >
                    Cancelar
                </button>
                <button
                    className="btn btn-secondary"
                    onClick={isEdit ? handleUpdateTerm : handleAddTerm}
                    disabled={saving}
                >
                    {saving ? (
                        <span className="loading loading-spinner loading-sm"></span>
                    ) : isEdit ? (
                        'Salvar'
                    ) : (
                        'Adicionar'
                    )}
                </button>
            </div>
        </div>
    )

    return (
        <div className="flex flex-col gap-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() =>
                        showPdfView
                            ? (setShowPdfView(false), setPdfBlob(null))
                            : navigate('/administrative/contracts')
                    }
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                        {showPdfView
                            ? 'Visualização do PDF'
                            : `Contrato #${contract.id}`}
                    </h1>
                    <p className="text-sm text-base-content/60 mt-1">
                        {showPdfView
                            ? 'Visualização em PDF das cláusulas'
                            : 'Gestão de cláusulas do contrato'}
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                    {showPdfView ? (
                        <>
                            <button
                                className="btn btn-secondary btn-sm gap-1"
                                onClick={handleDownloadPdf}
                                disabled={!pdfBlob}
                            >
                                <MdDownload className="text-lg" />
                                Baixar PDF
                            </button>
                            <button
                                className="btn btn-ghost btn-sm gap-1"
                                onClick={() => {
                                    setShowPdfView(false)
                                    setPdfBlob(null)
                                }}
                            >
                                <MdClose className="text-lg" />
                                Fechar
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="btn btn-ghost btn-sm gap-1"
                                onClick={handleGeneratePdf}
                                disabled={
                                    !contract.terms?.length || generatingPdf
                                }
                            >
                                {generatingPdf ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <BsFilePdf className="text-lg" />
                                )}
                                Ver PDF
                            </button>

                            {contract.status === 'DRAFT' && (
                                <button
                                    className="btn btn-success btn-sm gap-1"
                                    onClick={() =>
                                        setShowFinalizeModal(true)
                                    }
                                    disabled={!contract.terms?.length}
                                >
                                    <MdCheck className="text-lg" />
                                    Finalizar Contrato
                                </button>
                            )}

                            {contract.status === 'APPROVED' && (
                                <>
                                    <button
                                        className="btn btn-secondary btn-sm gap-1"
                                        onClick={() =>
                                            setShowSendToSignModal(true)
                                        }
                                    >
                                        <MdSend className="text-lg" />
                                        Enviar para Assinatura
                                    </button>
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={handleBackToDraft}
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <span className="loading loading-spinner loading-sm"></span>
                                        ) : (
                                            'Voltar para Rascunho'
                                        )}
                                    </button>
                                </>
                            )}

                            {(contract.status === 'SENT' ||
                                contract.status === 'SIGNED') && (
                                    <div
                                        className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg ${contract.status === 'SIGNED'
                                            ? 'text-success bg-success/10'
                                            : 'text-info bg-info/10'
                                            }`}
                                    >
                                        <MdDraw className="text-lg" />
                                        {contract.status === 'SIGNED'
                                            ? 'Contrato Assinado'
                                            : 'Aguardando Assinaturas'}
                                    </div>
                                )}

                            {canEditContract() && (
                                <button
                                    className="btn btn-secondary btn-sm gap-1"
                                    onClick={() => {
                                        resetTermForm()
                                        setShowAddTermModal(true)
                                    }}
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar Cláusula
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Conteúdo */}
            {showPdfView ? (
                <Suspense
                    fallback={
                        <div className="flex items-center justify-center py-12">
                            <span className="loading loading-spinner loading-lg text-secondary" />
                        </div>
                    }
                >
                    <ContractPDFViewer
                        pdfBlob={pdfBlob}
                        loading={generatingPdf}
                    />
                </Suspense>
            ) : contract.terms?.length === 0 ? (
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-12 text-center">
                    <BsFilePdf className="text-5xl text-secondary/40 mx-auto mb-4" />
                    <p className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma cláusula cadastrada
                    </p>
                    <p className="text-base-content/60 mb-6">
                        Clique em &quot;Adicionar Cláusula&quot; para começar
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {contract.terms.map(term => (
                        <div
                            key={term.id}
                            className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 hover:border-secondary/30 transition-colors"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                        {isHeaderOrFooter(term.type) ? (
                                            <span className="badge badge-sm badge-secondary">
                                                {getTermTypeLabel(term.type)}
                                            </span>
                                        ) : (
                                            <span className="badge badge-sm badge-secondary badge-outline">
                                                {getTermTypeLabel(term.type)}{' '}
                                                {term.number}
                                            </span>
                                        )}
                                        {/*<span
                                            className={`badge badge-sm ${term.status === 'APPROVED'
                                                ? 'badge-success'
                                                : term.status === 'REJECTED'
                                                    ? 'badge-error'
                                                    : 'badge-warning'
                                                }`}
                                        >
                                            {term.status === 'APPROVED'
                                                ? 'Aprovado'
                                                : term.status === 'REJECTED'
                                                    ? 'Rejeitado'
                                                    : 'Pendente'}
                                        </span>*/}
                                        {(term.fixed || term.adminOnly) && (
                                            <span className="badge badge-sm badge-info badge-outline">
                                                Protegida
                                            </span>
                                        )}
                                    </div>
                                    {!isHeaderOrFooter(term.type) &&
                                        term.title && (
                                            <h3 className="text-lg font-semibold text-base-content mb-2">
                                                {term.title}
                                            </h3>
                                        )}
                                    <p className="text-base-content/80 whitespace-pre-wrap">
                                        {term.content}
                                    </p>
                                </div>

                                {canEditContract() && (
                                    <div className="flex gap-1 ml-4 flex-shrink-0">
                                        <button
                                            className="btn btn-ghost btn-sm btn-square"
                                            onClick={() => openEditModal(term)}
                                            disabled={
                                                !canEditOrDeleteTerm(term)
                                            }
                                            title="Editar"
                                        >
                                            <MdEdit className="text-base" />
                                        </button>
                                        <button
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                            onClick={() =>
                                                openDeleteModal(term)
                                            }
                                            disabled={
                                                !canEditOrDeleteTerm(term)
                                            }
                                            title="Remover"
                                        >
                                            <MdDelete className="text-base" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Adicionar Cláusula */}
            <dialog className="modal" ref={addTermModalRef}>
                <div className="modal-box !max-w-2xl">
                    <h3 className="font-bold text-lg mb-4">
                        Adicionar Nova Cláusula
                    </h3>
                    {renderTermForm(false)}
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setShowAddTermModal(false)}>
                        close
                    </button>
                </form>
            </dialog>

            {/* Modal Editar Cláusula */}
            <dialog className="modal" ref={editTermModalRef}>
                <div className="modal-box !max-w-2xl">
                    <h3 className="font-bold text-lg mb-4">
                        Editar Cláusula
                    </h3>
                    {renderTermForm(true)}
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setShowEditTermModal(false)}>
                        close
                    </button>
                </form>
            </dialog>

            {/* Modal Confirmar Exclusão de Cláusula */}
            <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false)
                    setSelectedTerm(null)
                }}
                onConfirm={handleDeleteTerm}
                title="Confirmar Exclusão"
                message="Tem certeza que deseja remover esta cláusula?"
                confirmButtonText="Remover"
                loading={saving}
            />

            {/* Modal Finalizar Contrato */}
            <ConfirmModal
                isOpen={showFinalizeModal}
                onClose={() => setShowFinalizeModal(false)}
                onConfirm={handleFinalizeContract}
                title="Finalizar Contrato"
                message="Ao finalizar, o contrato ficará bloqueado para edição. Você poderá enviá-lo para assinatura eletrônica ou retorná-lo para rascunho. Deseja continuar?"
                confirmButtonText="Finalizar"
                confirmButtonClass="btn btn-success !border-0 !rounded-lg"
                loading={saving}
            />

            {/* Modal Enviar para Assinatura */}
            <ConfirmModal
                isOpen={showSendToSignModal}
                onClose={() => setShowSendToSignModal(false)}
                onConfirm={handleSendToSign}
                title="Enviar para Assinatura"
                message="O PDF do contrato será gerado e salvo como documento de assinatura. Você será redirecionado para adicionar os signatários e enviar para assinatura eletrônica."
                loading={sendingToSign}
                confirmButtonText="Continuar"
                confirmButtonClass="btn btn-secondary !border-0 !rounded-lg"
            />
        </div>
    )
}
